import { createElement } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useShallow } from "zustand/shallow";
import { sileo } from "sileo";
import {
  Armchair,
  CalendarCheck,
  CalendarX,
  DoorOpen,
  Undo2,
  UserCheck,
  UserX,
} from "lucide-react";
import type { MapMode } from "@/lib/map-engine";
import type {
  Guest,
  NewReservationDraft,
  Notification,
  Reservation,
  ReservationOrigin,
  ReservationStatus,
  RestaurantConfig,
  StatusTab,
  Table,
  TableGroupSuggestion,
  TableStatus,
  WaitlistSeatPickMove,
  ZoneConfig,
} from "./types";
import { seedRestaurantConfig } from "./seed";
import { confirmReservationTables, walkInOnlyZoneNames } from "./walk-in-zone-confirm-store";

/** A reservation that is still a booking: not seated, not a walk-in. Only
 *  these are kept out of walk-ins-only zones. */
function isBooking(r: { status: string; origin: string }): boolean {
  return r.status !== "sentado" && r.origin !== "walkin";
}
import {
  apiCancelReservation,
  apiCompleteReservation,
  apiCreateReservation,
  apiMarkNoShow,
  apiReassignTables,
  apiRevertCancel,
  apiRevertComplete,
  apiRevertNoShow,
  apiRevertSeat,
  apiSeatReservation,
  apiSwapTables,
  apiToggleConfirmed,
  apiUndoNoShow,
} from "../lib/reservation-api";
import { addHoursToTime, dayjs, nowFormatted, parseTime, todayStr } from "../utils/date";

// ── Icon helper ─────────────────────────────────────────────────
const icon = (C: React.ComponentType<{ size?: number; strokeWidth?: number }>) =>
  createElement(C, { size: 18, strokeWidth: 2.5 });

// ── Store interface ─────────────────────────────────────────────
export interface AppStore {
  // ── State ──────────────────────────────────────────────────────
  zones: ZoneConfig[];
  tables: Table[];
  reservations: Reservation[];
  guests: Guest[];
  restaurantConfig: RestaurantConfig;
  /** Legacy — kept for resetData compat. Sonner handles rendering. */
  notifications: Notification[];
  selectedDate: string;
  previewTime: string | null;
  /** In-sidebar surface for adding a guest to the waitlist. Mirrors the
   *  ``newReservationOpen`` flag and overlays the list the same way. */
  newWaitlistOpen: boolean;
  showSidebar: boolean;
  sidebarCollapsed: boolean;
  /** Active status tab in the sidebar list. Lifted here so other surfaces
   * (e.g. the desktop "Agregar a lista de espera" button) can react to it. */
  activeTab: StatusTab;
  highlightedReservationId: string | null;
  detailReservationId: string | null;
  /** Id of the waitlist entry whose detail panel is open in the sidebar.
   * Parallel to ``detailReservationId`` — the Espera tab opens its own
   * overlay so reservation and waitlist details never collide. */
  detailWaitlistId: string | null;
  /** Per-entry seat picks made via the TableExplorer (from the card or
   * the detail). Shared so both surfaces show the same chip + warnings
   * for the same entry until it's seated or removed from the queue. */
  waitlistSeatPicks: Record<string, { tableIds: string[]; moves: WaitlistSeatPickMove[] }>;
  /** Transient flag set by the map's "Reasignar mesa" chip (and any
   * other surface that wants the sidebar's DetailView to open straight
   * into edit mode). The DetailView snapshots and clears this on
   * match. */
  pendingEditReservationId: string | null;
  highlightedTableIds: string[];
  focusTableIds: string[];
  mapFitRequested: number; // increment to trigger fitToContent
  /** Global map interaction mode — mirrors MapEngine's MapMode. */
  mapMode: MapMode;
  /**
   * Per-location lock flag. Each sede has its own layout and its own sensible
   * pan/zoom, so letting the lock state leak across sedes would silently
   * enable/disable interaction for the wrong map after a switch.
   */
  mapLockedByLocation: Record<string, boolean>;
  /** When set, the map enters reassign mode — available tables glow blue
   *  and clicking one reassigns the reservation. */
  mapReassignMode: { reservationId: string; pax: number } | null;
  /** ``true`` while the sidebar detail panel is in edit mode. Mirrored
   *  here by the Sidebar so other surfaces (the dashboard "Nueva
   *  reserva" CTA, the mobile FAB) can hide their entry points and
   *  avoid stealing focus from the in-flight edit. */
  isEditingReservation: boolean;
  /** Visibility flag for the in-sidebar "Nueva reserva" surface. */
  newReservationOpen: boolean;
  /** In-flight draft for the in-sidebar new-reservation flow. Survives
   *  the sidebar closing/opening so accidental navigation doesn't lose
   *  what the operator typed; cleared on submit or explicit Descartar. */
  newReservationDraft: NewReservationDraft;
  /** Monotonic counter that increments every time an entry point asks
   *  to start a fresh reservation while the surface is already open.
   *  The surface watches this and either resets silently (pristine
   *  draft) or pops the discard confirm (dirty draft) so the operator
   *  doesn't get an inert ``+`` button or a silent wipe. */
  newReservationResetTick: number;
  /** Optional seed parked alongside a reset request. Set when an entry
   *  point with context (e.g. the map's time-travel free-mesa click)
   *  asks to re-open while the surface already holds an unrelated
   *  draft. The surface applies it after a confirmed discard so the
   *  operator never silently loses what they were typing. ``null``
   *  when the request is a plain "start over" (``+`` button). */
  pendingNewReservationSeed: Partial<NewReservationDraft> | null;
  /** Quote-request id being converted by the in-flight new reservation.
   *  Set when "Reservar" opens the surface from a cotización; threaded
   *  into the create payload so the backend excludes that quote's hold
   *  from the capacity check and converts the quote on success. Cleared
   *  on a normal open and on close/abandon so a plain reservation never
   *  carries a stale quote id. */
  convertingQuoteId: string | null;

  // ── Actions ────────────────────────────────────────────────────
  resetData: () => void;
  setMapMode: (mode: MapMode) => void;
  setMapLocked: (locationId: string, locked: boolean) => void;
  setMapReassignMode: (mode: { reservationId: string; pax: number } | null) => void;

  // Zone CRUD
  addZone: (zone: ZoneConfig) => void;
  updateZone: (id: string, changes: Partial<ZoneConfig>) => void;
  deleteZone: (id: string) => void;

  // Table CRUD
  addTable: (table: Table) => void;
  updateTable: (id: string, changes: Partial<Table>) => void;
  deleteTable: (id: string) => void;
  toggleNeighbor: (tableId: string, neighborId: string) => void;
  nextTableId: () => string;
  nextTableName: () => string;

  // Config
  updateConfig: (changes: Partial<RestaurantConfig>) => void;

  // Derived helpers (non-computed)
  isReservationSoon: (r: Reservation) => boolean;
  getTableStatus: (tableId: string) => TableStatus;
  getTableReservation: (tableId: string) => Reservation | undefined;
  isGuestRisk: (guestName: string) => boolean;

  // Notifications
  notify: (
    type: Notification["type"],
    title: string,
    message: string,
    _reservationId?: string,
    toastIcon?: React.ReactNode,
  ) => void;
  dismissNotification: (_id: string) => void;

  // UI state setters
  setSelectedDate: (date: string) => void;
  setPreviewTime: (time: string | null) => void;
  /** Open the in-sidebar new-reservation surface with an optional
   *  partial pre-fill. Used by entry points (dashboard CTA, map's
   *  free-mesa click) that already have some fields resolved. */
  openNewReservation: (seed?: Partial<NewReservationDraft>) => void;
  /** Close the surface AND discard the draft. Use after submit
   *  success, or after explicit Descartar from the confirm bar. */
  closeNewReservation: () => void;
  /** Set the quote-request id the in-flight new reservation should
   *  convert on save. ``null`` clears it. */
  setConvertingQuoteId: (id: string | null) => void;
  /** Discard the in-flight draft but keep the surface open. Used by
   *  the reset-tick path when the operator confirms "Descartar" on the
   *  re-open flow ("+", same surface). */
  resetNewReservationDraft: () => void;
  /** Clear the parked seed without resetting the draft. Called by the
   *  surface after it has consumed ``pendingNewReservationSeed`` (or
   *  when the operator cancels the discard confirm). */
  clearPendingNewReservationSeed: () => void;
  /** Merge a partial patch into the current draft. Cheap to call from
   *  field handlers; component owns its own debounce/throttle. */
  updateNewReservationDraft: (patch: Partial<NewReservationDraft>) => void;
  /** Open / close the waitlist surface that overlays the sidebar list.
   *  Mirrors ``openNewReservation`` / ``closeNewReservation`` for parity. */
  openNewWaitlist: () => void;
  closeNewWaitlist: () => void;
  setShowSidebar: (show: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setActiveTab: (tab: StatusTab) => void;
  setHighlightedReservationId: (id: string | null) => void;
  setDetailReservationId: (id: string | null) => void;
  setDetailWaitlistId: (id: string | null) => void;
  setWaitlistSeatPick: (
    entryId: string,
    pick: { tableIds: string[]; moves: WaitlistSeatPickMove[] },
  ) => void;
  clearWaitlistSeatPick: (entryId: string) => void;
  setPendingEditReservationId: (id: string | null) => void;
  setIsEditingReservation: (value: boolean) => void;
  setHighlightedTableIds: (ids: string[]) => void;
  setFocusTableIds: (ids: string[]) => void;
  requestMapFit: () => void;

  // Reservation lifecycle
  createReservation: (
    data: {
      guestId?: string;
      guestName: string;
      guestPhone?: string;
      guestEmail?: string;
      pax: number;
      date: string;
      time: string;
      durationMinutes?: number;
      internalNotes?: string;
      /** Guest-facing notes (the "Notas del cliente" textarea on the
       *  in-sidebar Nueva reserva surface). Maps to ``notes`` on the
       *  backend schema; distinct from host-only ``internalNotes``. */
      notes?: string;
      tags?: string[];
      features?: string[];
      occasion?: string;
      isVip?: boolean;
      tableId?: string;
      tableIds?: string[];
      /** Pre-emptive moves the operator picked alongside the new
       *  reservation. The backend's create endpoint applies them in the
       *  same transaction so a redistribute plan from the explorer
       *  goes through atomically. */
      moves?: Array<{ reservation_id: string; tables: string[] }>;
      origin?: ReservationOrigin;
      preferredZone?: string;
      /** Quote-request UUID this reservation converts. Threaded straight
       *  through to ``apiCreateReservation`` so the backend excludes the
       *  quote's hold from the cap check and converts it on success. */
      quoteRequestId?: string;
    },
    /** Optional async hooks. ``onApiSuccess`` fires once the backend
     *  confirms (including after override-and-retry), ``onApiCancel``
     *  fires on dismissed override dialogs or hard failures. The legacy
     *  modal ignores both; the in-sidebar Nueva reserva uses them to
     *  keep the draft alive when an override is cancelled. */
    callbacks?: {
      onApiSuccess?: () => void;
      onApiCancel?: () => void;
    },
  ) => Reservation;
  createWalkIn: (
    tableIds: string | string[],
    pax: number,
    guestName: string,
    locationTz?: string,
    extra?: {
      guestId?: string;
      guestPhone?: string;
      guestEmail?: string;
      /** Override the default 3 h walk-in duration. Used when an
       *  upcoming reservation forces an early end so the two parties
       *  don't physically overlap on the table. */
      endTime?: string;
    },
  ) => Reservation;
  createAutoReservation: (data: {
    guestName: string;
    guestPhone?: string;
    guestEmail?: string;
    pax: number;
    date: string;
    time: string;
    origin: "google" | "whatsapp" | "web";
    preferredZone?: string;
  }) => Reservation;
  editReservation: (id: string, changes: Partial<Reservation>) => void;
  /** Resolves ``false`` when staff backed out of a walk-ins-only zone. */
  reassignTable: (reservationId: string, newTableIds: string[]) => Promise<boolean>;
  swapTables: (resAId: string, resBId: string) => Promise<boolean>;
  cancelReservation: (id: string, reason: string) => void;
  markSeated: (id: string) => void;
  markCompleted: (id: string) => void;
  markNoShow: (id: string) => void;
  revertNoShow: (id: string) => void;
  unseatReservation: (id: string) => void;
  toggleConfirmedByHost: (id: string) => void;

  // Table grouping
  findTableCombinations: (
    pax: number,
    date: string,
    time: string,
    preferredZone?: string,
  ) => TableGroupSuggestion[];
  getFreeTables: (date: string, time: string) => Table[];

  // Guest management
  findGuest: (query: string) => Guest[];
}

// ── Zustand store ───────────────────────────────────────────────
export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => {
      // ── Internal helpers ──────────────────────────────────────

      function rangesOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
        const s1 = parseTime(start1).valueOf();
        let e1 = parseTime(end1).valueOf();
        const s2 = parseTime(start2).valueOf();
        let e2 = parseTime(end2).valueOf();
        if (e1 <= s1) {
          e1 += 24 * 60 * 60 * 1000;
        }
        if (e2 <= s2) {
          e2 += 24 * 60 * 60 * 1000;
        }
        return s1 < e2 && e1 > s2;
      }

      function getTableStatusAt(tableId: string, date: string, time: string): TableStatus {
        const { reservations, restaurantConfig } = get();
        const newEnd = addHoursToTime(time, restaurantConfig.timeLimitMinutes / 60);
        const hasReservation = reservations.some(
          (r) =>
            r.date === date &&
            r.tableIds.includes(tableId) &&
            !["cancelada", "completada", "noshow"].includes(r.status) &&
            rangesOverlap(time, newEnd, r.time, r.endTime),
        );
        return hasReservation ? "reserved" : "free";
      }

      function upsertGuest(name: string, email?: string, phone?: string) {
        const { guests } = get();
        const existing = guests.find(
          (g) => (phone && g.phone === phone) || (email && g.email === email) || g.name === name,
        );
        if (existing) {
          set((state) => ({
            guests: state.guests.map((g) =>
              g.id === existing.id ? { ...g, totalVisits: g.totalVisits + 1 } : g,
            ),
          }));
        } else {
          set((state) => ({
            guests: [
              ...state.guests,
              {
                attended: 0,
                avgPartySize: 0,
                cancellations: 0,
                clientSince: dayjs()
                  .format("MMMM YYYY")
                  .replace(/^\w/, (c) => c.toUpperCase()),
                email: email || "",
                favoriteTable: "",
                id: "g" + Date.now().toString(36),
                name,
                noShows: 0,
                notes: "",
                phone: phone || "",
                rating: 0,
                totalVisits: 1,
              },
            ],
          }));
        }
      }

      function updateGuestStats(guestName: string, event: "attended" | "noshow" | "cancelled") {
        set((state) => ({
          guests: state.guests.map((g) => {
            if (g.name !== guestName) {
              return g;
            }
            return {
              ...g,
              attended: event === "attended" ? g.attended + 1 : g.attended,
              cancellations: event === "cancelled" ? g.cancellations + 1 : g.cancellations,
              noShows: event === "noshow" ? g.noShows + 1 : g.noShows,
            };
          }),
        }));
      }

      const UNDO_WINDOW = 7000;

      /**
       * Dangerous action: optimistic UI update with undo.
       * The backend commit fires immediately so the action is durable across
       * reloads. If the user clicks "Deshacer", we revert the local state and
       * call the matching `revert-*` endpoint to undo on the backend too.
       */
      function dangerousAction(opts: {
        title: string;
        message: string;
        toastIcon?: React.ReactNode;
        prevReservations: Reservation[];
        prevGuests?: Guest[];
        apiCall: () => Promise<unknown> | void;
        revertCall: () => Promise<unknown> | void;
        sideEffect?: () => void;
      }) {
        opts.sideEffect?.();
        // Track the original request so undo can wait for it before
        // attempting the revert — otherwise the revert may race the commit
        // and 409 because the BE hasn't transitioned yet.
        const inFlight = Promise.resolve(opts.apiCall());

        const toastId = sileo.action({
          button: {
            onClick: () => {
              sileo.dismiss(toastId);
              set({
                reservations: opts.prevReservations,
                ...(opts.prevGuests ? { guests: opts.prevGuests } : {}),
              });
              inFlight.finally(() => opts.revertCall());
            },
            title: "Deshacer",
          },
          description: opts.message,
          duration: UNDO_WINDOW,
          icon: opts.toastIcon,
          title: opts.title,
        });
      }

      return {
        // ── Initial state ───────────────────────────────────────
        zones: [],
        tables: [],
        reservations: [],
        guests: [],
        restaurantConfig: seedRestaurantConfig,
        notifications: [],
        selectedDate: todayStr(),
        previewTime: null,
        newWaitlistOpen: false,
        showSidebar: false,
        sidebarCollapsed: false,
        activeTab: "reservadas",
        highlightedReservationId: null,
        detailReservationId: null,
        detailWaitlistId: null,
        waitlistSeatPicks: {},
        pendingEditReservationId: null,
        highlightedTableIds: [],
        focusTableIds: [],
        mapFitRequested: 0,
        mapMode: "view",
        mapLockedByLocation: {},
        mapReassignMode: null,
        isEditingReservation: false,
        newReservationOpen: false,
        newReservationResetTick: 0,
        pendingNewReservationSeed: null,
        convertingQuoteId: null,
        newReservationDraft: {
          guestId: null,
          guestName: "",
          guestPhone: "",
          guestEmail: "",
          pax: null,
          date: "",
          time: "",
          zone: "",
          tableIds: [],
          features: [],
          isVip: false,
          notes: "",
          moves: [],
        },

        // ── resetData ───────────────────────────────────────────
        resetData() {
          set({
            notifications: [],
            reservations: [],
            restaurantConfig: { ...seedRestaurantConfig },
          });
        },

        setMapMode(mode) {
          set({ mapMode: mode });
        },

        setMapLocked(locationId, locked) {
          set((state) => ({
            mapLockedByLocation: { ...state.mapLockedByLocation, [locationId]: locked },
          }));
        },
        setMapReassignMode(mode) {
          set({ mapReassignMode: mode });
        },

        // ── Zone CRUD ───────────────────────────────────────────
        addZone(zone) {
          set((state) => ({ zones: [...state.zones, zone] }));
        },

        updateZone(id, changes) {
          set((state) => ({
            zones: state.zones.map((z) => (z.id === id ? { ...z, ...changes } : z)),
          }));
        },

        deleteZone(id) {
          set((state) => ({
            tables: state.tables.map((t) => (t.zoneId === id ? { ...t, zoneId: "" } : t)),
            zones: state.zones.filter((z) => z.id !== id),
          }));
        },

        // ── Table CRUD ──────────────────────────────────────────
        addTable(table) {
          set((state) => ({ tables: [...state.tables, table] }));
        },

        updateTable(id, changes) {
          set((state) => ({
            tables: state.tables.map((t) => (t.id === id ? { ...t, ...changes } : t)),
          }));
        },

        deleteTable(id) {
          set((state) => ({
            tables: state.tables
              .filter((t) => t.id !== id)
              .map((t) => ({
                ...t,
                neighbors: t.neighbors.filter((n) => n !== id),
              })),
          }));
        },

        toggleNeighbor(tableId, neighborId) {
          set((state) => ({
            tables: state.tables.map((t) => {
              if (t.id === tableId) {
                const has = t.neighbors.includes(neighborId);
                return {
                  ...t,
                  neighbors: has
                    ? t.neighbors.filter((n) => n !== neighborId)
                    : [...t.neighbors, neighborId],
                };
              }
              if (t.id === neighborId) {
                const has = t.neighbors.includes(tableId);
                return {
                  ...t,
                  neighbors: has
                    ? t.neighbors.filter((n) => n !== tableId)
                    : [...t.neighbors, tableId],
                };
              }
              return t;
            }),
          }));
        },

        nextTableId() {
          return crypto.randomUUID();
        },

        nextTableName() {
          const re = /^Mesa (\d+)$/;
          let max = 0;
          for (const t of get().tables) {
            const m = re.exec(t.name);
            if (m) {
              const n = Number(m[1]);
              if (n > max) {
                max = n;
              }
            }
          }
          return `Mesa ${max + 1}`;
        },

        // ── Config ──────────────────────────────────────────────
        updateConfig(changes) {
          set((state) => ({
            restaurantConfig: { ...state.restaurantConfig, ...changes },
          }));
        },

        // ── Derived helpers ─────────────────────────────────────
        /** True when reservation datetime is within proximityMinutes from
         * "now". When ``previewTime`` is set the store is in time-travel
         * mode and "now" is the operator-picked instant on
         * ``selectedDate``; otherwise it's wall-clock now.
         *
         * Anchors to r.date + r.time so a tomorrow-19:00 reservation
         * isn't flagged "soon" at today-18:30 (parseTime alone pins to
         * today). */
        isReservationSoon(r) {
          const { restaurantConfig, previewTime, selectedDate } = get();
          const now = previewTime
            ? dayjs(`${selectedDate} ${previewTime}`, "YYYY-MM-DD h:mm A")
            : dayjs();
          const resDateTime = dayjs(`${r.date} ${r.time}`, "YYYY-MM-DD h:mm A");
          const diffMs = resDateTime.valueOf() - now.valueOf();
          return (
            diffMs <= restaurantConfig.proximityMinutes * 60 * 1000 &&
            diffMs > -(restaurantConfig.timeLimitMinutes * 60 * 1000)
          );
        },

        getTableStatus(tableId) {
          const { isReservationSoon, tables, zones, previewTime, getTableReservation } = get();
          const table = tables.find((t) => t.id === tableId);
          const zone = table ? zones.find((z) => z.id === table.zoneId) : undefined;
          const disabled = table?.isAvailable === false || zone?.isAvailable === false;

          // Time-travel mode: classify the table the same way the live
          // map does, but anchored to the simulated instant. A
          // reservation whose [time, endTime) window contains the
          // simulated time → ``occupied``. A reservation that hasn't
          // started yet but is within ``proximityMinutes`` →
          // ``reserved``. Otherwise ``free`` (or ``unavailable`` when
          // the mesa or its zone is disabled).
          if (previewTime) {
            const r = getTableReservation(tableId);
            if (!r) return disabled ? "unavailable" : "free";
            const sim = dayjs(
              `${get().selectedDate} ${previewTime}`,
              "YYYY-MM-DD h:mm A",
            ).valueOf();
            const start = dayjs(`${r.date} ${r.time}`, "YYYY-MM-DD h:mm A").valueOf();
            return sim >= start ? "occupied" : "reserved";
          }

          if (disabled) {
            const today = get().reservations.filter(
              (r) => r.date === todayStr() && r.status !== "cancelada",
            );
            const seated = today.find(
              (r) => r.tableIds.includes(tableId) && r.status === "sentado",
            );
            if (seated) return "occupied";
            const reserved = today.find(
              (r) =>
                r.tableIds.includes(tableId) && r.status === "reservada" && isReservationSoon(r),
            );
            if (reserved) return "reserved";
            return "unavailable";
          }
          const today = get().reservations.filter(
            (r) => r.date === todayStr() && r.status !== "cancelada",
          );
          const seated = today.find((r) => r.tableIds.includes(tableId) && r.status === "sentado");
          if (seated) {
            return "occupied";
          }
          const reserved = today.find(
            (r) => r.tableIds.includes(tableId) && r.status === "reservada" && isReservationSoon(r),
          );
          if (reserved) {
            return "reserved";
          }
          return "free";
        },

        getTableReservation(tableId) {
          const { previewTime, selectedDate, reservations, restaurantConfig } = get();
          // Time-travel mode: prefer whichever reservation's
          // [time, endTime) window contains the simulated instant; if
          // there's no current window, fall back to the next one
          // starting within ``proximityMinutes`` (the "reservada pronto"
          // surface). Status is ignored — a 22:00 BOOKED row "shows up"
          // on the mesa at 22:00 even though the party hasn't been sat
          // in reality.
          if (previewTime) {
            const sim = dayjs(`${selectedDate} ${previewTime}`, "YYYY-MM-DD h:mm A");
            const onTable = reservations.filter(
              (r) =>
                r.tableIds.includes(tableId) &&
                r.status !== "cancelada" &&
                r.status !== "noshow" &&
                r.date === selectedDate,
            );
            const active = onTable.find((r) => {
              const start = dayjs(`${r.date} ${r.time}`, "YYYY-MM-DD h:mm A");
              const end = dayjs(`${r.date} ${r.endTime}`, "YYYY-MM-DD h:mm A");
              return sim.valueOf() >= start.valueOf() && sim.valueOf() < end.valueOf();
            });
            if (active) return active;
            // Nothing active right now — surface the imminent one so the
            // map still highlights "se va a sentar en N min".
            const proximityMs = restaurantConfig.proximityMinutes * 60 * 1000;
            const upcoming = onTable
              .map((r) => ({
                r,
                start: dayjs(`${r.date} ${r.time}`, "YYYY-MM-DD h:mm A").valueOf(),
              }))
              .filter(({ start }) => start >= sim.valueOf() && start - sim.valueOf() <= proximityMs)
              .toSorted((a, b) => a.start - b.start);
            return upcoming[0]?.r;
          }
          return reservations.find(
            (r) =>
              r.date === todayStr() &&
              r.tableIds.includes(tableId) &&
              ["sentado", "reservada"].includes(r.status),
          );
        },

        /** Risk flag: guest has >= 2 no-shows in history */
        isGuestRisk(guestName) {
          const guest = get().guests.find((g) => g.name === guestName || g.phone === guestName);
          return guest ? guest.noShows >= 2 : false;
        },

        // ── Notifications ───────────────────────────────────────
        notify(type, title, message, _reservationId, toastIcon) {
          const opts = { description: message, icon: toastIcon, title };
          if (type === "success") {
            sileo.success(opts);
          } else if (type === "warning") {
            sileo.warning(opts);
          } else if (type === "error") {
            sileo.error(opts);
          } else {
            sileo.info(opts);
          }
        },

        dismissNotification(id) {
          sileo.dismiss(id);
        },

        // ── UI state setters ────────────────────────────────────
        setSelectedDate(date) {
          // Keep ``previewTime`` across date changes: time-travel mode
          // simulates ``selectedDate`` + ``previewTime``, so the
          // operator can browse another day at the same simulated
          // hour. The pill at the top of the map surfaces both pieces
          // when the date differs from today.
          set({ selectedDate: date });
        },
        setPreviewTime(time) {
          set({ previewTime: time });
        },
        openNewReservation(seed) {
          const state = get();
          // Already open: don't clobber the in-flight draft. Bump the
          // reset-tick so the surface decides what to do — pristine
          // drafts reset (silently if no seed, or with the seed
          // applied if there is one), dirty drafts pop the discard
          // confirm and apply the seed only after a confirmed
          // discard. Parking the seed in ``pendingNewReservationSeed``
          // lets the surface pick it up after the confirm without
          // each entry point having to know about the open state.
          if (state.newReservationOpen) {
            set({
              newReservationResetTick: state.newReservationResetTick + 1,
              pendingNewReservationSeed: seed ?? null,
              sidebarCollapsed: false,
              convertingQuoteId: null,
            });
            return;
          }
          // Merge the seed onto a fresh draft so a stale prior session
          // doesn't bleed into the new surface. The default ``date``
          // tracks the calendar the operator is browsing — but if
          // that's in the past (looking at a historical day), we snap
          // forward to today since you can't book backwards in time.
          const today = todayStr();
          const browsing = state.selectedDate || today;
          const defaultDate = browsing < today ? today : browsing;
          const blank: NewReservationDraft = {
            guestId: null,
            guestName: "",
            guestPhone: "",
            guestEmail: "",
            pax: null,
            date: defaultDate,
            time: "",
            zone: "",
            tableIds: [],
            features: [],
            isVip: false,
            notes: "",
            moves: [],
          };
          // Expand the collapsed desktop rail so the surface lands
          // somewhere visible — don't touch ``showSidebar`` here
          // (that opens the tablet/portrait Sheet, which would
          // double-stack on top of the always-visible desktop
          // sidebar on lg+ screens). Callers that need the Sheet
          // (tablet-portrait + button) open it themselves.
          set({
            newReservationOpen: true,
            newReservationDraft: { ...blank, ...seed },
            sidebarCollapsed: false,
            convertingQuoteId: null,
          });
        },
        closeNewReservation() {
          // Hide + discard. Submit success and explicit Descartar both
          // call this — the draft is intentionally not persisted past
          // a session.
          set({
            newReservationOpen: false,
            newReservationDraft: {
              guestId: null,
              guestName: "",
              guestPhone: "",
              guestEmail: "",
              pax: null,
              date: "",
              time: "",
              zone: "",
              tableIds: [],
              features: [],
              isVip: false,
              notes: "",
              moves: [],
            },
            convertingQuoteId: null,
          });
        },
        setConvertingQuoteId(id) {
          set({ convertingQuoteId: id });
        },
        resetNewReservationDraft() {
          // Clear the in-flight draft back to defaults without closing
          // the surface. Used when the operator clicks "+" again on a
          // dirty draft and confirms "Sí, descartar" in the alert.
          const today = todayStr();
          const browsing = get().selectedDate || today;
          const defaultDate = browsing < today ? today : browsing;
          set({
            newReservationDraft: {
              guestId: null,
              guestName: "",
              guestPhone: "",
              guestEmail: "",
              pax: null,
              date: defaultDate,
              time: "",
              zone: "",
              tableIds: [],
              features: [],
              isVip: false,
              notes: "",
              moves: [],
            },
          });
        },
        clearPendingNewReservationSeed() {
          set({ pendingNewReservationSeed: null });
        },
        updateNewReservationDraft(patch) {
          set((state) => ({
            newReservationDraft: { ...state.newReservationDraft, ...patch },
          }));
        },
        openNewWaitlist() {
          // Expand the collapsed desktop rail so the overlay lands somewhere
          // visible — same pattern as ``openNewReservation``.
          set({ newWaitlistOpen: true, sidebarCollapsed: false });
        },
        closeNewWaitlist() {
          set({ newWaitlistOpen: false });
        },
        setShowSidebar(show) {
          set({ showSidebar: show });
        },
        setSidebarCollapsed(collapsed) {
          set({ sidebarCollapsed: collapsed });
        },
        setActiveTab(tab) {
          set({ activeTab: tab });
        },
        setHighlightedReservationId(id) {
          set({ highlightedReservationId: id });
        },
        setPendingEditReservationId(id) {
          set({ pendingEditReservationId: id });
        },
        setIsEditingReservation(value) {
          set({ isEditingReservation: value });
        },
        setDetailReservationId(id) {
          set({ detailReservationId: id });
        },
        setWaitlistSeatPick(entryId, pick) {
          set((state) => ({
            waitlistSeatPicks: { ...state.waitlistSeatPicks, [entryId]: pick },
          }));
        },
        clearWaitlistSeatPick(entryId) {
          set((state) => {
            if (!(entryId in state.waitlistSeatPicks)) return state;
            const { [entryId]: _drop, ...rest } = state.waitlistSeatPicks;
            return { waitlistSeatPicks: rest };
          });
        },
        setDetailWaitlistId(id) {
          set({ detailWaitlistId: id });
        },
        setHighlightedTableIds(ids) {
          set({ highlightedTableIds: ids });
        },
        setFocusTableIds(ids) {
          set({ focusTableIds: ids });
        },
        requestMapFit() {
          set((s) => ({ mapFitRequested: s.mapFitRequested + 1 }));
        },

        // ── §6.1 Create Reservation ─────────────────────────────
        createReservation(data, callbacks) {
          const id = "r" + Date.now().toString(36);
          // Only honour explicit user picks. When nothing was chosen we let
          // the backend's suggest-tables algorithm decide — guessing locally
          // with store ids would either duplicate that work or leak synthetic
          // seed ids into a payload the backend rejects as non-UUID.
          const assignedTables = data.tableIds?.length
            ? data.tableIds
            : data.tableId
              ? [data.tableId]
              : [];
          const durationHours = (data.durationMinutes ?? 180) / 60;
          const endTime = addHoursToTime(data.time, durationHours);

          const reservation: Reservation = {
            createdAt: Date.now(),
            date: data.date,
            endTime,
            guestEmail: data.guestEmail || "",
            guestName: data.guestName,
            guestPhone: data.guestPhone || "",
            history: [
              {
                timestamp: Date.now(),
                action: "created",
                detail: `Reserva creada · ${data.pax} pax · ${data.origin || "seimseim"}`,
              },
            ],
            id,
            isConfirmedByHost: false,
            isVip: data.isVip || false,
            notes: data.notes || "",
            internalNotes: data.internalNotes || "",
            origin: data.origin || "seimseim",
            pax: data.pax,
            preferredZone: data.preferredZone || "",
            seatedAt: "",
            // The backend assigns the real pin level on create based on
            // origin + preferred zone; the local placeholder is the
            // conservative ``none`` so the engine treats the row as
            // flexible until the server response lands.
            pinLevel: "none",
            status: "reservada",
            tableIds: assignedTables,
            tags: data.tags || [],
            features: data.features || [],
            occasion: data.occasion || "none",
            time: data.time,
          };

          set((state) => ({
            reservations: [...state.reservations, reservation],
          }));
          upsertGuest(data.guestName, data.guestEmail, data.guestPhone);
          apiCreateReservation(
            id,
            {
              ...data,
              tableIds: assignedTables,
              endTime: endTime,
            },
            callbacks,
          );

          const tableNames = assignedTables
            .map((tid) => get().tables.find((t) => t.id === tid)?.name)
            .filter(Boolean);
          const originLabel = (
            {
              google: "Google",
              phone: "Llamada",
              seimseim: "Panel",
              walkin: "Walk-in",
              web: "Web",
              whatsapp: "WhatsApp",
              agent: "Agente",
            } as Record<ReservationOrigin, string>
          )[reservation.origin];

          if (assignedTables.length > 0) {
            get().notify(
              "success",
              `Reserva ${reservation.time}`,
              `${data.guestName} (${data.pax}p) → ${tableNames.join(" + ")} · ${originLabel}`,
              id,
              icon(CalendarCheck),
            );
          } else {
            // No explicit pick — the server runs its own table-grouping and
            // the fire-and-forget response hydrates the reservation with the
            // final assignment (or leaves it unassigned if nothing fits).
            get().notify(
              "info",
              `Reserva ${reservation.time}`,
              `${data.guestName} (${data.pax}p) — Asignando mesa… · ${originLabel}`,
              id,
              icon(CalendarCheck),
            );
          }

          return reservation;
        },

        // ── §6.2 Walk-In ────────────────────────────────────────
        createWalkIn(tableIds, pax, guestName, locationTz, extra) {
          const now = nowFormatted(locationTz);
          const id = "w" + Date.now().toString(36);
          const ids = Array.isArray(tableIds) ? tableIds : [tableIds];
          // Walk-ins are always "now" — bind to today regardless of the
          // sidebar's selected date, so the live map sees them immediately.
          // Date/time are recorded in the **location's** TZ so the optimistic
          // record matches what the backend will return after roundtrip
          // (``fromApiReservation`` also converts to location TZ for display).
          const date = todayStr(locationTz);

          const walkInTableNames = ids
            .map((tid) => get().tables.find((t) => t.id === tid)?.name)
            .filter(Boolean)
            .join(" + ");
          const effectiveEndTime = extra?.endTime ?? addHoursToTime(now, 3);
          const reservation: Reservation = {
            createdAt: Date.now(),
            date,
            endTime: effectiveEndTime,
            guestEmail: extra?.guestEmail ?? "",
            guestName: guestName || "Walk-in",
            guestPhone: extra?.guestPhone ?? "",
            history: [
              {
                timestamp: Date.now(),
                action: "walkin",
                detail: walkInTableNames
                  ? `Walk-in sentado · ${walkInTableNames}`
                  : "Walk-in sentado",
              },
            ],
            id,
            isConfirmedByHost: false,
            isVip: false,
            notes: "",
            internalNotes: "",
            origin: "walkin",
            pax,
            preferredZone: "",
            seatedAt: now,
            // Walk-ins are physically seated → the engine never moves them.
            pinLevel: "table",
            status: "sentado",
            tableIds: ids,
            tags: [],
            features: [],
            time: now,
          };

          set((state) => ({
            reservations: [...state.reservations, reservation],
          }));
          apiCreateReservation(id, {
            guestName: guestName || "Walk-in",
            guestId: extra?.guestId,
            guestPhone: extra?.guestPhone,
            guestEmail: extra?.guestEmail,
            pax,
            date,
            time: now,
            // Only forward ``endTime`` when the caller explicitly set
            // it (e.g. the overbook auto-cap). Otherwise let
            // ``apiCreateReservation``'s default branch use the precise
            // ``startsAt + 3h`` it always has — re-parsing a formatted
            // local time string here would drop the seconds.
            endTime: extra?.endTime,
            tableIds: ids,
            origin: "walkin",
            status: "sentado",
          });

          const tableNames = ids
            .map((tid) => get().tables.find((t) => t.id === tid)?.name)
            .filter(Boolean)
            .join(" + ");
          get().notify(
            "success",
            "Walk-in sentado",
            `${guestName || "Walk-in"} (${pax}p) → ${tableNames}`,
            id,
            icon(UserCheck),
          );

          return reservation;
        },

        // ── §6.3 Auto-sync (Google/WhatsApp/Web) ────────────────
        createAutoReservation(data) {
          return get().createReservation({
            ...data,
            origin: data.origin,
            preferredZone: data.preferredZone,
          });
        },

        // ── §6.4 Edit Reservation ───────────────────────────────
        editReservation(id, changes) {
          set((state) => ({
            reservations: state.reservations.map((r) => {
              if (r.id !== id) {
                return r;
              }
              const detail = Object.keys(changes).join(", ");
              return {
                ...r,
                ...changes,
                history: [
                  ...r.history,
                  {
                    action: "edited",
                    detail: `Editado: ${detail}`,
                    timestamp: Date.now(),
                  },
                ],
              };
            }),
          }));
        },

        // ── §6.5 Reassign Table ─────────────────────────────────
        reassignTable(reservationId, newTableIds) {
          const { tables, reservations, zones } = get();
          const target = reservations.find((r) => r.id === reservationId);
          // A booking placed by hand in a walk-ins-only zone gets a second
          // look first. Someone already seated there is a walk-in in all
          // but name, so they move without asking.
          const needsConfirm =
            target !== undefined &&
            isBooking(target) &&
            walkInOnlyZoneNames(newTableIds, tables, zones).length > 0;
          if (needsConfirm) {
            return confirmReservationTables(newTableIds, tables, zones).then((ok) => {
              if (ok) applyReassign();
              return ok;
            });
          }
          applyReassign();
          return Promise.resolve(true);

          function applyReassign() {
            set((state) => ({
              reservations: state.reservations.map((r) => {
                if (r.id !== reservationId) {
                  return r;
                }
                const oldTables = r.tableIds
                  .map((tid) => tables.find((t) => t.id === tid)?.name || "Mesa")
                  .join(" + ");
                const newTables = newTableIds
                  .map((tid) => tables.find((t) => t.id === tid)?.name || "Mesa")
                  .join(" + ");
                return {
                  ...r,
                  history: [
                    ...r.history,
                    {
                      timestamp: Date.now(),
                      action: "reassigned",
                      detail: `${oldTables} → ${newTables}`,
                    },
                  ],
                  tableIds: newTableIds,
                };
              }),
            }));
            apiReassignTables(reservationId, newTableIds);
          }
        },

        swapTables(resAId, resBId) {
          const { tables, reservations, zones } = get();
          const resA = reservations.find((r) => r.id === resAId);
          const resB = reservations.find((r) => r.id === resBId);
          if (!resA || !resB) return Promise.resolve(false);

          const aOldIds = [...resA.tableIds];
          const bOldIds = [...resB.tableIds];
          // Each side lands on the other's tables: ask if a booking would
          // end up in a walk-ins-only zone.
          const landing = [
            ...(isBooking(resA) ? bOldIds : []),
            ...(isBooking(resB) ? aOldIds : []),
          ];
          if (walkInOnlyZoneNames(landing, tables, zones).length > 0) {
            return confirmReservationTables(landing, tables, zones).then((ok) => {
              if (ok) applySwap();
              return ok;
            });
          }
          applySwap();
          return Promise.resolve(true);

          function applySwap() {
            const nameOf = (ids: string[]) =>
              ids.map((tid) => tables.find((t) => t.id === tid)?.name || "Mesa").join(" + ");

            set((state) => ({
              reservations: state.reservations.map((r) => {
                if (r.id === resAId) {
                  return {
                    ...r,
                    tableIds: bOldIds,
                    history: [
                      ...r.history,
                      {
                        timestamp: Date.now(),
                        action: "swapped" as const,
                        detail: `${nameOf(aOldIds)} ↔ ${nameOf(bOldIds)} (${resB.guestName})`,
                      },
                    ],
                  };
                }
                if (r.id === resBId) {
                  return {
                    ...r,
                    tableIds: aOldIds,
                    history: [
                      ...r.history,
                      {
                        timestamp: Date.now(),
                        action: "swapped" as const,
                        detail: `${nameOf(bOldIds)} ↔ ${nameOf(aOldIds)} (${resA.guestName})`,
                      },
                    ],
                  };
                }
                return r;
              }),
            }));

            apiSwapTables(resAId, resBId);
          }
        },

        // ── §6.6 Cancel Reservation ─────────────────────────────
        cancelReservation(id, reason) {
          const prevReservations = [...get().reservations];
          const prevGuests = [...get().guests];
          set((state) => ({
            reservations: state.reservations.map((r) => {
              if (r.id !== id) {
                return r;
              }
              return {
                ...r,
                history: [
                  ...r.history,
                  {
                    timestamp: Date.now(),
                    action: "cancelled",
                    detail: `Cancelada: ${reason || "Sin motivo"}`,
                  },
                ],
                status: "cancelada" as ReservationStatus,
              };
            }),
          }));
          const r = get().reservations.find((res) => res.id === id);
          dangerousAction({
            apiCall: () => apiCancelReservation(id, reason),
            revertCall: () => apiRevertCancel(id),
            message: `${r?.guestName} — ${reason || "Sin motivo"}`,
            prevGuests,
            prevReservations,
            sideEffect: () => updateGuestStats(r?.guestName || "", "cancelled"),
            title: "Reserva cancelada",
            toastIcon: icon(CalendarX),
          });
        },

        // ── §6.7 Mark as Seated ─────────────────────────────────
        markSeated(id) {
          const prevReservations = [...get().reservations];
          const now = nowFormatted();
          set((state) => ({
            reservations: state.reservations.map((r) => {
              if (r.id !== id) {
                return r;
              }
              return {
                ...r,
                status: "sentado" as ReservationStatus,
                seatedAt: now,
                endTime: addHoursToTime(now, 3), // V2: 3h from SEATED time
                history: [
                  ...r.history,
                  {
                    action: "seated",
                    detail: `Sentado a las ${now}`,
                    timestamp: Date.now(),
                  },
                ],
              };
            }),
          }));
          const r = get().reservations.find((res) => res.id === id);
          const seatedNames = r?.tableIds
            .map((tid) => get().tables.find((t) => t.id === tid)?.name)
            .filter(Boolean)
            .join(" + ");
          dangerousAction({
            apiCall: () => apiSeatReservation(id),
            revertCall: () => apiRevertSeat(id),
            message: `${r?.guestName} → ${seatedNames}`,
            prevReservations,
            title: "Comensal sentado",
            toastIcon: icon(Armchair),
          });
        },

        // ── §6.8 Mark as Completed ──────────────────────────────
        markCompleted(id) {
          const prevReservations = [...get().reservations];
          const prevGuests = [...get().guests];
          set((state) => ({
            reservations: state.reservations.map((r) => {
              if (r.id !== id) {
                return r;
              }
              return {
                ...r,
                history: [
                  ...r.history,
                  {
                    timestamp: Date.now(),
                    action: "completed",
                    detail: "Comensal se retiró",
                  },
                ],
                status: "completada" as ReservationStatus,
              };
            }),
          }));
          const r = get().reservations.find((res) => res.id === id);
          const freedNames = r?.tableIds
            .map((tid) => get().tables.find((t) => t.id === tid)?.name)
            .filter(Boolean)
            .join(" + ");
          dangerousAction({
            apiCall: () => apiCompleteReservation(id),
            revertCall: () => apiRevertComplete(id),
            message: `${r?.guestName} — ${freedNames} disponible`,
            prevGuests,
            prevReservations,
            sideEffect: () => updateGuestStats(r?.guestName || "", "attended"),
            title: "Mesa liberada",
            toastIcon: icon(DoorOpen),
          });
        },

        // ── §6.9 No-Show ────────────────────────────────────────
        markNoShow(id) {
          const prevReservations = [...get().reservations];
          const prevGuests = [...get().guests];
          set((state) => ({
            reservations: state.reservations.map((r) => {
              if (r.id !== id) {
                return r;
              }
              return {
                ...r,
                history: [
                  ...r.history,
                  {
                    timestamp: Date.now(),
                    action: "noshow",
                    detail: "No-show",
                  },
                ],
                status: "noshow" as ReservationStatus,
              };
            }),
          }));
          const r = get().reservations.find((res) => res.id === id);
          const noshowNames = r?.tableIds
            .map((tid) => get().tables.find((t) => t.id === tid)?.name)
            .filter(Boolean)
            .join(" + ");
          dangerousAction({
            apiCall: () => apiMarkNoShow(id),
            revertCall: () => apiUndoNoShow(id),
            message: `${r?.guestName} — ${noshowNames} liberada`,
            prevGuests,
            prevReservations,
            sideEffect: () => updateGuestStats(r?.guestName || "", "noshow"),
            title: "No-show",
            toastIcon: icon(UserX),
          });
        },

        /** Revertir no-show: NO_SHOW → SEATED. Delegates the entire flow
         *  to ``apiRevertNoShow`` which calls the backend; on conflict
         *  (mesa ocupada) the structured 409 opens the RevertTablesDialog
         *  with server-validated alternatives so the operator picks one
         *  and the reactivation lands on the new tables atomically. No
         *  local pre-check or optimistic table mutation here — those
         *  used to PATCH the row while still in NO_SHOW (which the
         *  backend rejects) and to surface mesas that hadn't been
         *  filtered against the actual floor state. */
        revertNoShow(id) {
          const r = get().reservations.find((res) => res.id === id);
          if (!r) {
            return;
          }
          apiRevertNoShow(id, r.guestName || "la reserva");
        },

        /** Undo a seat: SEATED → BOOKED. Used by the short-lived
         *  "Revertir Sentar" affordance right after seating a party.
         *
         *  Walk-ins that came from the waitlist take a different path: the
         *  server deletes the reservation and returns the entry to ``waiting``.
         *  Flipping to ``reservada`` locally would flash a phantom row in
         *  "Reservadas" until the invalidate landed, so when the row carries
         *  ``originWaitlistEntryId`` we drop it from the store outright. */
        unseatReservation(id) {
          const r = get().reservations.find((res) => res.id === id);
          if (!r || r.status !== "sentado") {
            return;
          }
          const fromWaitlist = !!r.originWaitlistEntryId;
          if (fromWaitlist) {
            set((state) => ({
              reservations: state.reservations.filter((res) => res.id !== id),
            }));
          } else {
            set((state) => ({
              reservations: state.reservations.map((res) => {
                if (res.id !== id) {
                  return res;
                }
                return {
                  ...res,
                  status: "reservada" as ReservationStatus,
                  seatedAt: "",
                  endTime: addHoursToTime(res.time, 3),
                  history: [
                    ...res.history,
                    {
                      timestamp: Date.now(),
                      action: "reverted",
                      detail: "Sentar revertido — vuelve a reservada",
                    },
                  ],
                };
              }),
            }));
          }
          const tableNames = r.tableIds
            .map((tid) => get().tables.find((t) => t.id === tid)?.name)
            .filter(Boolean)
            .join(" + ");
          // The waitlist branch surfaces its own "Volvió a la cola" toast
          // from ``apiRevertSeat``; skip the generic one to avoid a double.
          if (!fromWaitlist) {
            get().notify(
              "success",
              "Sentar revertido",
              `${r.guestName} vuelve a reservadas${tableNames ? ` · ${tableNames}` : ""}`,
              id,
              icon(Undo2),
            );
          }
          apiRevertSeat(id);
        },

        // ── Confirmed tag toggle ────────────────────────────────
        toggleConfirmedByHost(id) {
          set((state) => ({
            reservations: state.reservations.map((r) => {
              if (r.id !== id) {
                return r;
              }
              const newVal = !r.isConfirmedByHost;
              return {
                ...r,
                history: [
                  ...r.history,
                  {
                    timestamp: Date.now(),
                    action: "confirmed_tag",
                    detail: newVal
                      ? "Marcado como confirmado por hostess"
                      : "Tag confirmado removido",
                  },
                ],
                isConfirmedByHost: newVal,
              };
            }),
          }));
          apiToggleConfirmed(id);
        },

        // ── §5 Table Grouping ───────────────────────────────────
        findTableCombinations(pax, date, time, preferredZone) {
          const { tables } = get();
          const results: TableGroupSuggestion[] = [];
          const available = tables.filter((t) => getTableStatusAt(t.id, date, time) === "free");

          // If zone preference, sort to prioritize that zone
          const sorted = preferredZone
            ? [...available].toSorted(
                (a, b) =>
                  (a.zoneId === preferredZone ? -1 : 1) - (b.zoneId === preferredZone ? -1 : 1),
              )
            : available;

          // Singles
          for (const t of sorted) {
            if (t.maxCapacity >= pax) {
              results.push({
                tableIds: [t.id],
                totalCapacity: t.maxCapacity,
                wastedCapacity: t.maxCapacity - pax,
              });
            }
          }

          // Pairs
          for (const t1 of available) {
            for (const nId of t1.neighbors) {
              const t2 = available.find((t) => t.id === nId);
              if (t2 && t2.id > t1.id) {
                const cap = t1.maxCapacity + t2.maxCapacity;
                if (cap >= pax) {
                  results.push({
                    tableIds: [t1.id, t2.id],
                    totalCapacity: cap,
                    wastedCapacity: cap - pax,
                  });
                }
              }
            }
          }

          // Triples
          for (const t1 of available) {
            for (const n1 of t1.neighbors) {
              const t2 = available.find((t) => t.id === n1);
              if (!t2 || t2.id <= t1.id) {
                continue;
              }
              for (const n2 of t2.neighbors) {
                if (n2 === t1.id) {
                  continue;
                }
                const t3 = available.find((t) => t.id === n2);
                if (!t3 || t3.id <= t2.id) {
                  continue;
                }
                const cap = t1.maxCapacity + t2.maxCapacity + t3.maxCapacity;
                if (cap >= pax) {
                  results.push({
                    tableIds: [t1.id, t2.id, t3.id],
                    totalCapacity: cap,
                    wastedCapacity: cap - pax,
                  });
                }
              }
            }
          }

          return results.toSorted((a, b) => {
            // Prefer combos in the preferred zone
            if (preferredZone) {
              const aInZone = a.tableIds.every(
                (id) => tables.find((t) => t.id === id)?.zoneId === preferredZone,
              );
              const bInZone = b.tableIds.every(
                (id) => tables.find((t) => t.id === id)?.zoneId === preferredZone,
              );
              if (aInZone && !bInZone) {
                return -1;
              }
              if (!aInZone && bInZone) {
                return 1;
              }
            }
            return a.wastedCapacity - b.wastedCapacity;
          });
        },

        getFreeTables(date, time) {
          return get().tables.filter((t) => getTableStatusAt(t.id, date, time) === "free");
        },

        // ── Guest management ────────────────────────────────────
        findGuest(query) {
          const q = query.toLowerCase();
          return get().guests.filter(
            (g) =>
              g.name.toLowerCase().includes(q) ||
              g.email.toLowerCase().includes(q) ||
              g.phone.includes(q),
          );
        },
      };
    },
    {
      name: "seimseim-store",
      partialize: (state) => ({
        restaurantConfig: state.restaurantConfig,
        mapLockedByLocation: state.mapLockedByLocation,
      }),
    },
  ),
);

// ── Selector hooks (replaces computed signals) ──────────────────

/** Non-cancelled reservations for the sidebar's selected date. Sidebar, lists
 * and forms follow this; the live floor map uses ``useLiveReservations``. */
export const useTodayReservations = () =>
  useAppStore(
    useShallow((state) =>
      state.reservations.filter((r) => r.date === state.selectedDate && r.status !== "cancelada"),
    ),
  );

/** Every reservation for the selected date, including cancelled ones. Use
 * this in list UIs that want a full day view (sidebar "Historial" toggle);
 * most other consumers should stick to ``useTodayReservations``. */
export const useTodayReservationsAll = () =>
  useAppStore(
    useShallow((state) => state.reservations.filter((r) => r.date === state.selectedDate)),
  );

/** Non-cancelled reservations anchored to the real calendar day (not the
 * sidebar's selected date). The floor map uses this so its live state stays
 * on "today" while the sidebar is free to navigate other days. */
export const useLiveReservations = () =>
  useAppStore(
    useShallow((state) =>
      state.reservations.filter((r) => r.date === todayStr() && r.status !== "cancelada"),
    ),
  );

/** Reservations grouped by time for the selected date */
export const useReservationsByTime = () =>
  useAppStore(
    useShallow((state) => {
      const today = state.reservations.filter(
        (r) => r.date === state.selectedDate && r.status !== "cancelada",
      );
      const groups: Record<string, Reservation[]> = {};
      for (const r of today) {
        (groups[r.time] ??= []).push(r);
      }
      return groups;
    }),
  );

/** Restaurant stats: table counts and capacity */
export const useStats = () =>
  useAppStore(
    useShallow((state) => {
      const all = state.tables;
      const statuses = all.map((t) => state.getTableStatus(t.id));
      const free = statuses.filter((s) => s === "free").length;
      const occupied = statuses.filter((s) => s === "occupied").length;
      const reserved = statuses.filter((s) => s === "reserved").length;
      const totalCapacity = all.reduce((s, t) => s + t.maxCapacity, 0);
      const usedCapacity = state.reservations
        .filter((r) => r.date === state.selectedDate && r.status === "sentado")
        .reduce((s, r) => s + r.pax, 0);
      return {
        free,
        occupied,
        reserved,
        total: all.length,
        totalCapacity,
        usedCapacity,
      };
    }),
  );

// ── Convenience re-exports for backward compat ──────────────────
// These allow components to call store actions without using the hook directly.
// Usage: import { addZone } from './store'  →  addZone(zone)

export const resetData = () => useAppStore.getState().resetData();
export const addZone = (zone: ZoneConfig) => useAppStore.getState().addZone(zone);
export const updateZone = (id: string, changes: Partial<ZoneConfig>) =>
  useAppStore.getState().updateZone(id, changes);
export const deleteZone = (id: string) => useAppStore.getState().deleteZone(id);
export const addTable = (table: Table) => useAppStore.getState().addTable(table);
export const updateTable = (id: string, changes: Partial<Table>) =>
  useAppStore.getState().updateTable(id, changes);
export const deleteTable = (id: string) => useAppStore.getState().deleteTable(id);
export const toggleNeighbor = (tableId: string, neighborId: string) =>
  useAppStore.getState().toggleNeighbor(tableId, neighborId);
export const nextTableId = () => useAppStore.getState().nextTableId();
export const nextTableName = () => useAppStore.getState().nextTableName();
export const updateConfig = (changes: Partial<RestaurantConfig>) =>
  useAppStore.getState().updateConfig(changes);
export const isReservationSoon = (r: Reservation) => useAppStore.getState().isReservationSoon(r);
export const setPreviewTime = (time: string | null) => useAppStore.getState().setPreviewTime(time);
export const setPendingEditReservationId = (id: string | null) =>
  useAppStore.getState().setPendingEditReservationId(id);
export const setIsEditingReservation = (value: boolean) =>
  useAppStore.getState().setIsEditingReservation(value);
export const openNewWaitlist = () => useAppStore.getState().openNewWaitlist();
export const closeNewWaitlist = () => useAppStore.getState().closeNewWaitlist();
export const getTableStatus = (tableId: string) => useAppStore.getState().getTableStatus(tableId);
export const getTableReservation = (tableId: string) =>
  useAppStore.getState().getTableReservation(tableId);
export const isGuestRisk = (guestName: string) => useAppStore.getState().isGuestRisk(guestName);
export const notify = (
  type: Notification["type"],
  title: string,
  message: string,
  _reservationId?: string,
  toastIcon?: React.ReactNode,
) => useAppStore.getState().notify(type, title, message, _reservationId, toastIcon);
export const dismissNotification = (id: string) => useAppStore.getState().dismissNotification(id);
export const createReservation = (
  data: Parameters<AppStore["createReservation"]>[0],
  callbacks?: Parameters<AppStore["createReservation"]>[1],
) => useAppStore.getState().createReservation(data, callbacks);
export const createWalkIn = (
  tableIds: string | string[],
  pax: number,
  guestName: string,
  locationTz?: string,
  extra?: {
    guestId?: string;
    guestPhone?: string;
    guestEmail?: string;
    endTime?: string;
  },
) => useAppStore.getState().createWalkIn(tableIds, pax, guestName, locationTz, extra);
export const createAutoReservation = (data: Parameters<AppStore["createAutoReservation"]>[0]) =>
  useAppStore.getState().createAutoReservation(data);
export const editReservation = (id: string, changes: Partial<Reservation>) =>
  useAppStore.getState().editReservation(id, changes);
export const reassignTable = (reservationId: string, newTableIds: string[]) =>
  useAppStore.getState().reassignTable(reservationId, newTableIds);
export const swapTables = (resAId: string, resBId: string) =>
  useAppStore.getState().swapTables(resAId, resBId);
export const cancelReservation = (id: string, reason: string) =>
  useAppStore.getState().cancelReservation(id, reason);
export const markSeated = (id: string) => useAppStore.getState().markSeated(id);
export const markCompleted = (id: string) => useAppStore.getState().markCompleted(id);
export const markNoShow = (id: string) => useAppStore.getState().markNoShow(id);
export const revertNoShow = (id: string) => useAppStore.getState().revertNoShow(id);
export const unseatReservation = (id: string) => useAppStore.getState().unseatReservation(id);
export const toggleConfirmedByHost = (id: string) =>
  useAppStore.getState().toggleConfirmedByHost(id);
export const findTableCombinations = (
  pax: number,
  date: string,
  time: string,
  preferredZone?: string,
) => useAppStore.getState().findTableCombinations(pax, date, time, preferredZone);
export const getFreeTables = (date: string, time: string) =>
  useAppStore.getState().getFreeTables(date, time);
export const findGuest = (query: string) => useAppStore.getState().findGuest(query);
export const setFocusTableIds = (ids: string[]) => useAppStore.getState().setFocusTableIds(ids);
export const requestMapFit = () => useAppStore.getState().requestMapFit();
export const setMapMode = (mode: MapMode) => useAppStore.getState().setMapMode(mode);
